import { FC, useContext, useRef } from "react";
import { ExportProviderAvailabilityType, NetworkProviderComparisonType, NetworkProviderFilterType } from "@app/exports/enumerations";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import ExportService from "@app/exports/exportService";
import { ExportAdequacyServicingProvidersModel } from "@app/exports/exportModels";
import { NextClosestProviderAction, PatientVolumeLabelEnum } from "@app/projects/enumerators";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { ReportCustomizationContext } from "@app/report-customization/build-reports/ReportCustomizationContext";
import { ColumnConfigurationSection } from "@app/report-customization/build-reports/components/ColumnConfigurationSection";
import { GenerateSection } from "@app/report-customization/build-reports/components/GenerateSection";
import { PciFiltersSection } from "@app/report-customization/build-reports/components/PciFiltersSection";
import { ProviderSection } from "@app/report-customization/build-reports/components/ProviderSection";
import { ServiceAreaSection } from "@app/report-customization/build-reports/components/ServiceAreaSection";
import { SpecialtySection } from "@app/report-customization/build-reports/components/SpecialtySection";
import { getPciFilterExportFields, handleToastSuccess, handleToastError, reportTypeToColumnsEndpointMap, shouldShowPciFiltersSection } from "@app/report-customization/build-reports/helpers/helpers";
import { ReportCustomizationReducerActions } from "@app/report-customization/build-reports/interfaces/reportCustomizationReducerActionsEnum";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";

export const ServicingProvidersReport: FC = () => {
  const rcContext = useContext(ReportCustomizationContext);
  const exportService = useRef(new ExportService()).current;
  const currentTimeslice = rcContext.findTimeslice(rcContext.state.timeslice);

  const getExportModel = () => {
    const newComparisonTypes = [];
    if (rcContext.state.showAddedProviders)
      newComparisonTypes.push(NetworkProviderComparisonType.Added);
    if (rcContext.state.showMatchedProviders)
      newComparisonTypes.push(NetworkProviderComparisonType.Matched);
    if (rcContext.state.showRemovedProviders)
      newComparisonTypes.push(NetworkProviderComparisonType.Removed);

    const { primaryId, secondaryId } = rcContext.getSelectedRunConfigurationIdsInChronologicalOrder();

    const servicingProviderModel: ExportAdequacyServicingProvidersModel = {
      ...getPciFilterExportFields(!!currentTimeslice?.showPciData, rcContext.state.reportFormat, rcContext.state),
      appendPatternOfCareProviders: rcContext.state.appendPatternOfCareProviders,
      comparedProjectRunConfigurationId: secondaryId,
      customProjectId: parseInt(rcContext.routeParams.projectId),
      eMailOnSuccess: rcContext.state.shouldEmailLink,
      exportName: rcContext.state.filename,
      formatType: rcContext.state.fileFormat,
      hsdFormat: rcContext.state.reportFormat,
      impactProviderType: rcContext.state.providerSource === ProviderNetworkTypeEnum.Prospect
        || rcContext.state.providerSource === ProviderNetworkTypeEnum.Market
        || rcContext.state.providerSource === ProviderNetworkTypeEnum.NetNew ? rcContext.state.providerFilter as ExportProviderAvailabilityType : null,
      includeActiveRegionSets: rcContext.state.includeActive,
      includeApprovedRegionSets: rcContext.state.includeApproved,
      includeCargoColumns: rcContext.state.includeCargo,
      includeFacilitySpecialties: rcContext.state.includeFacilitySpecialties,
      includePendingRegionSets: rcContext.state.includePending,
      includePhysicianSpecialties: rcContext.state.includePhysicianSpecialties,
      isZipAnalysis: false,
      networkAssessmentAnalysisTypes: rcContext.state.networkAssessmentAnalysisTypes,
      networkProviderFilterType: rcContext.state.providerSource === ProviderNetworkTypeEnum.Network ? rcContext.state.providerFilter as NetworkProviderFilterType : null,
      nextClosestProviderAction: rcContext.state.providerSource === ProviderNetworkTypeEnum.NextClosestProviders
        ? NextClosestProviderAction.FilterToNextClosestProvidersOnly
        : rcContext.state.appendNextClosestProviders
          ? NextClosestProviderAction.AppendNextClosestProviders
          : NextClosestProviderAction.None,
      projectRunConfigurationId: primaryId,
      projectURL: `${window.customUIAbsolutePath}/projects/${rcContext.routeParams.projectId}`,
      providerClaimsVolumeFilter: PatientVolumeLabelEnum.Unknown,
      providerComparisonTypes: newComparisonTypes,
      providerIds: rcContext.routeState.providerIds,
      providerNetworkType: rcContext.state.providerSource === ProviderNetworkTypeEnum.NextClosestProviders ? ProviderNetworkTypeEnum.Network : rcContext.state.providerSource,
      regionSetGapFilter: rcContext.state.serviceAreaGapsType,
      regionSetIds: rcContext.state.selectedCounties.map((sa) => parseInt(sa)),
      reportCustomizationId: rcContext.state.selectedPreset?.id,
      runAsynchronous: true,
      runFrequency: rcContext.state.runFrequency,
      sharedUsers: rcContext.state.sharedUsers,
      specialtyGapFilter: rcContext.state.specialtyGapsType,
      specialtyGroupCodes: rcContext.state.selectedSpecialties.map((sp) => sp.key),
    };

    return servicingProviderModel;
  };

  // event handlers
  const handleGenerateClick = () => {
    rcContext.dispatch({ payload: { isGenerating: true }, type: ReportCustomizationReducerActions.ChangeIsGenerating });

    exportService.postExportAdequacyServicingProviders(getExportModel())
      .then(() => handleToastSuccess(rcContext.state.filename),
        () => handleToastError(rcContext.state.filename)
      ).then(() => {
        rcContext.dispatch({ payload: { isGenerating: false }, type: ReportCustomizationReducerActions.ChangeIsGenerating });
      });
  }

  return (
    <>
      <div className="container ms-0">
        <ServiceAreaSection />

        <SpecialtySection />

        <ProviderSection />

        {shouldShowPciFiltersSection(!!currentTimeslice?.showPciData, rcContext.state.reportFormat) && (
          <PciFiltersSection
            showCareJourneyScores={!!currentTimeslice?.showCareJourneyScores}
            showLocationConfidenceScores={!!currentTimeslice?.showLocationConfidenceScores}
            showMotiveAppropriatenessScore={!!currentTimeslice?.showMotiveAppropriatenessScore}
            showTricareClaimsVolumeColumn={!!currentTimeslice?.showTricareClaimsVolumeColumn}
            showVaClaimsVolumeColumn={!!currentTimeslice?.showVaClaimsVolumeColumn}
          />
        )}

        <ColumnConfigurationSection
          columnsEndpoint={reportTypeToColumnsEndpointMap.get(ReportType.AdequacyServicingProvider)}
          exportModel={getExportModel()}
          presets={rcContext.state.presets}
          selectedPreset={rcContext.state.selectedPreset}
          reportType={rcContext.getReportType()}
          onPresetChange={rcContext.changeSelectedPreset}
          onPresetsRefresh={rcContext.changeLoadedPresets}
          reportFormat={rcContext.state.reportFormat}
        />

        <QESCheckbox
          containerClassName="mt-3 "
          label="Include cargo column information"
          checked={rcContext.state.includeCargo}
          onChange={(e) => rcContext.dispatch({ payload: { includeCargo: e.target.checked }, type: ReportCustomizationReducerActions.ChangeIncludeCargo })}
          testId="report-customization__include-cargo"
        />
      </div>
      <GenerateSection
        settingsAreValid={rcContext.validateSettings()}
        onGenerate={handleGenerateClick}
      />
    </>
  );
}